import { useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Icon,
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2CardBody,
  V2CardHeader,
  V2FormField,
  V2MetaStrip,
  V2Table,
  V2Tag,
  V2ToastStack,
  type V2TableColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type {
  FinanceHandoffLine,
  MovementDetail,
  MovementLineDetail,
} from '../../lib/types/inventory.js';
import type { AccountListResponse } from '../../lib/types/finance.js';
import { amountColumnHeader, formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { RelatedJournalsCard } from '../finance/RelatedJournalsCard.js';
import {
  MOVEMENT_STATUS_META,
  MOVEMENT_TYPE_LABEL,
  formatQuantity,
  metaOf,
} from './inventoryDisplay.js';
import { useToasts } from './useInventoryUi.js';
import { useReceiveTransfer } from './useReceiveTransfer.js';
import './inventory.css';

interface LineRow extends MovementLineDetail {
  readonly id: string;
}

interface HandoffRow extends FinanceHandoffLine {
  readonly id: string;
}

/** Where the breadcrumb sends the reader back to, per movement type. */
const LIST_ROUTE: Record<string, { label: string; path: string }> = {
  receipt: { label: 'رسیدها', path: 'receipts' },
  issue: { label: 'خروج‌ها', path: 'issues' },
  transfer: { label: 'انتقال‌ها', path: 'transfers' },
  count_adjustment: { label: 'تعدیلات', path: 'adjustments' },
};

const PARTY_LABEL: Record<string, string> = {
  receipt: 'تأمین‌کننده',
  issue: 'مشتری / تحویل‌گیرنده',
};

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Inventory.dc.html §03 Movement Detail — with finance
 *   (moving-average) handoff
 * status: KIT_CONFORMANT — `Available = On-hand − Reserved − Blocked`
 *   preserved exactly as returned by the API; the finance-handoff lines carry only a productId,
 *   so the product name beside each is resolved from the document's own physical lines.
 * state reference: ready · permission_denied · not_found · network_error
 *
 * ERP-150 (added 2026-09-18): a manual "ثبت سند حسابداری" action, wiring
 * the real `POST .../finance/inventory-handoffs` endpoint
 * (`recordInventoryFinanceHandoff`,
 * packages/modules/accounting/src/inventory-handoff.ts) — genuinely
 * safe to click more than once: the domain looks up an existing journal
 * by `source_reference = movementId` regardless of idempotency key, so a
 * second call always returns `replayed: true` with the same journal
 * rather than duplicating it. `RelatedJournalsCard` below already
 * surfaces whether a handoff journal exists (it matches by the same
 * `movementId` source reference) — recording one and refreshing that
 * card is the whole loop; this page does not re-derive that state
 * itself.
 *
 * This is deliberately scoped to one movement, not the cross-movement
 * reconciliation monitor the design's fuller "inventory-to-accounting
 * bridge" intent implies: no endpoint lists movements missing a handoff,
 * their errors, or a retry queue — only this single-movement
 * create-or-replay action exists server-side. That broader view needs a
 * new backend list endpoint and is out of scope here (flagged as a
 * follow-up, not built by inventing one).
 */
export function InventoryMovementDetailPage() {
  const { unit, label } = useAmountDisplay();
  const { slug, movementId } = useParams<{ slug: string; movementId: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { toasts, push, dismiss } = useToasts();
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  const result = useApiGet<MovementDetail>(
    slug && movementId ? `${base}/api/v1/inventory/movements/${movementId}` : null,
    [slug, movementId, detailRefreshKey],
  );
  const glAccounts = useApiGet<AccountListResponse>(
    slug ? `${base}/api/v1/finance/accounts` : null,
    [slug],
  );
  const postableGlAccounts = useMemo(
    () =>
      glAccounts.status === 'ready'
        ? [...glAccounts.data.items]
            .filter((a) => a.isPostable && a.status === 'active')
            .sort((a, b) => a.code.localeCompare(b.code))
        : [],
    [glAccounts],
  );

  const [debitAccountId, setDebitAccountId] = useState('');
  const [creditAccountId, setCreditAccountId] = useState('');
  const [recording, setRecording] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [recordResult, setRecordResult] = useState<{
    readonly provisionalNumber: string;
    readonly replayed: boolean;
  } | null>(null);
  const [journalsRefreshKey, setJournalsRefreshKey] = useState(0);

  const receiveTransfer = useReceiveTransfer(base, () => {
    setDetailRefreshKey((k) => k + 1);
    push('وصول انتقال ثبت شد.');
  });

  const lineRows = useMemo<readonly LineRow[]>(
    () =>
      result.status === 'ready'
        ? (result.data.lines ?? []).map((l) => ({ ...l, id: l.movementLineId }))
        : [],
    [result],
  );
  const productByLine = useMemo(() => new Map(lineRows.map((l) => [l.productId, l])), [lineRows]);

  if (!slug || !movementId) return null;

  async function onRecordHandoff(event: FormEvent) {
    event.preventDefault();
    if (recording) return;
    setRecordError(null);
    setRecordResult(null);
    if (!debitAccountId || !creditAccountId) {
      setRecordError('حساب بدهکار و بستانکار الزامی است.');
      return;
    }
    setRecording(true);
    try {
      const res = await apiPost<{ provisionalNumber: string; replayed: boolean }>(
        `${base}/api/v1/finance/inventory-handoffs`,
        { movementId, debitAccountId, creditAccountId },
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      setRecordResult({ provisionalNumber: res.provisionalNumber, replayed: res.replayed });
      setJournalsRefreshKey((k) => k + 1);
    } catch (err) {
      setRecordError(bannerMessageFrom(err, 'ثبت سند انجام نشد.'));
    } finally {
      setRecording(false);
    }
  }

  const movementType = result.status === 'ready' ? result.data.movementType : '';
  const isTransfer = movementType === 'transfer';
  const isReceipt = movementType === 'receipt';

  const lineColumns: readonly V2TableColumn<LineRow>[] = [
    {
      key: 'row',
      header: '#',
      align: 'end',
      render: (line) => <span className="sk-num">{line.rowNumber.toLocaleString('fa-IR')}</span>,
    },
    {
      key: 'product',
      header: 'کالا',
      render: (line) => (
        <span className="inv-cell">
          <span className="inv-cell__main">{line.productName}</span>
          <span className="inv-cell__sub ltr">{line.productCode}</span>
        </span>
      ),
    },
    { key: 'uom', header: 'واحد', render: (line) => line.uomName ?? line.uomCode ?? '—' },
    {
      key: 'where',
      header: 'انبار / محل',
      render: (line) => (
        <span className="inv-cell">
          <span className="inv-cell__main">{line.warehouseName}</span>
          <span className="inv-cell__sub">{line.locationName ?? line.locationCode}</span>
        </span>
      ),
    },
    ...(isTransfer
      ? [
          {
            key: 'dest',
            header: 'انبار / محل مقصد',
            render: (line: LineRow) => (
              <span className="inv-cell">
                <span className="inv-cell__main">
                  {line.counterWarehouseName ?? line.counterWarehouseCode ?? '—'}
                </span>
                <span className="inv-cell__sub">
                  {line.counterLocationName ?? line.counterLocationCode ?? '—'}
                </span>
              </span>
            ),
          },
        ]
      : []),
    {
      key: 'quantity',
      header: 'مقدار',
      align: 'end',
      render: (line) => (
        <span className="sk-num">
          {formatQuantity({
            numerator: line.quantityNumerator,
            denominator: line.quantityDenominator,
          })}
        </span>
      ),
    },
    ...(isReceipt
      ? [
          {
            key: 'unitCost',
            header: amountColumnHeader('بهای واحد', unit),
            align: 'end' as const,
            render: (line: LineRow) => (
              <span className="sk-num">
                {line.unitCostRial ? formatRialFa(line.unitCostRial, unit) : '—'}
              </span>
            ),
          },
        ]
      : []),
    {
      key: 'tracking',
      header: 'بهر / سریال',
      render: (line) =>
        line.lotCode || line.serialNumber ? (
          <span className="inv-chips">
            {line.lotCode ? <V2Tag>بهر: {line.lotCode}</V2Tag> : null}
            {line.serialNumber ? <V2Tag>سریال: {line.serialNumber}</V2Tag> : null}
          </span>
        ) : (
          <span>—</span>
        ),
    },
  ];

  const handoffColumns: readonly V2TableColumn<HandoffRow>[] = [
    {
      key: 'productId',
      header: 'کالا',
      render: (row) => {
        const line = productByLine.get(row.productId);
        return line ? (
          <span className="inv-cell">
            <span className="inv-cell__main">{line.productName}</span>
            <span className="inv-cell__sub ltr">{line.productCode}</span>
          </span>
        ) : (
          <span>—</span>
        );
      },
    },
    {
      key: 'quantity',
      header: 'تعداد',
      align: 'end',
      render: (row) => (
        <span className="sk-num">
          {formatQuantity({
            numerator: row.quantityNumerator,
            denominator: row.quantityDenominator,
          })}
        </span>
      ),
    },
    {
      key: 'valueRial',
      header: amountColumnHeader('ارزش', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.valueRial, unit)}</span>,
    },
  ];

  const listRoute = LIST_ROUTE[movementType];
  const typeLabel = MOVEMENT_TYPE_LABEL[movementType] ?? 'سند انبار';

  return (
    <div className="inv-page">
      <div className="sk-breadcrumb">
        <Link to={`${base}/inventory`}>انبار</Link>
        {listRoute ? (
          <>
            <span>/</span>
            <Link to={`${base}/inventory/${listRoute.path}`}>{listRoute.label}</Link>
          </>
        ) : null}
        <span>/</span>
        <b className="ltr">{result.status === 'ready' ? result.data.movementNumber : '…'}</b>
      </div>

      {result.status === 'loading' ? <LoadingState /> : null}
      {result.status === 'error' ? (
        <StatePanel
          variant={
            result.error.status === 403
              ? 'permission_denied'
              : result.error.status === 404
                ? 'not_found'
                : 'network_error'
          }
          title={
            result.error.status === 403
              ? 'دسترسی ندارید'
              : result.error.status === 404
                ? 'پیدا نشد'
                : 'قابل نمایش نیست'
          }
        />
      ) : null}

      {receiveTransfer.error ? (
        <V2Alert tone="danger" onClose={receiveTransfer.clearError}>
          {receiveTransfer.error}
        </V2Alert>
      ) : null}

      {result.status === 'ready' ? (
        <>
          <div className="inv-detail-head">
            <h1 className="inv-detail-head__title">
              {typeLabel} <span className="ltr">{result.data.movementNumber}</span>
            </h1>
            <div className="inv-detail-head__actions">
              {result.data.status === 'in_transit' ? (
                <V2Button
                  variant="primary"
                  icon="check"
                  disabled={receiveTransfer.busyId === result.data.movementId}
                  onClick={() => void receiveTransfer.receive(result.data.movementId)}
                >
                  {receiveTransfer.busyId === result.data.movementId
                    ? 'در حال ثبت وصول…'
                    : 'تأیید وصول در انبار مقصد'}
                </V2Button>
              ) : null}
              <Link
                to={`${base}/inventory/movements/${result.data.movementId}/print`}
                target="_blank"
                className="sk-btn sk-btn--secondary sk-btn--md inv-btn-link"
              >
                <Icon name="invoice" size="sm" />
                <span>چاپ برگه رسمی انبار</span>
              </Link>
            </div>
          </div>

          <V2Card>
            <V2CardHeader icon="inventory" title="مشخصات سند" subtitle={typeLabel} />
            <V2MetaStrip
              items={[
                {
                  label: 'شمارهٔ سند',
                  value: <span className="ltr sk-num">{result.data.movementNumber}</span>,
                },
                { label: 'نوع سند', value: typeLabel },
                {
                  label: 'تاریخ سند',
                  value: (
                    <span className="sk-num">{displayJalaliDate(result.data.businessDate)}</span>
                  ),
                },
                {
                  label: PARTY_LABEL[movementType] ?? 'طرف‌حساب',
                  value: result.data.partyName ? (
                    <span className="inv-cell">
                      <span className="inv-cell__main">{result.data.partyName}</span>
                      <span className="inv-cell__sub ltr">{result.data.partyCode}</span>
                    </span>
                  ) : (
                    (result.data.partyCode ?? '—')
                  ),
                },
                {
                  label: 'شمارهٔ سفارش / مرجع',
                  value: <span className="ltr sk-num">{result.data.sourceReference ?? '—'}</span>,
                },
                {
                  label: 'وضعیت سند',
                  value: (
                    <V2Badge
                      tone={metaOf(MOVEMENT_STATUS_META, result.data.status ?? 'posted').tone}
                    >
                      {metaOf(MOVEMENT_STATUS_META, result.data.status ?? 'posted').label}
                    </V2Badge>
                  ),
                },
                {
                  label: 'ارزش کل (میانگین موزون)',
                  value: result.data.financeHandoff ? (
                    <span className="sk-num">
                      {formatRialFa(result.data.financeHandoff.totalValueRial, unit)} {label}
                    </span>
                  ) : (
                    'در انتظار وصول / انتقال'
                  ),
                },
              ]}
            />
          </V2Card>

          {lineRows.length > 0 ? (
            <V2Card>
              <V2CardHeader icon="layers" title="اقلام فیزیکی سند انبار" />
              <V2Table columns={lineColumns} rows={lineRows} zebra />
            </V2Card>
          ) : null}

          {result.data.financeHandoff ? (
            <>
              <V2Card>
                <V2CardHeader icon="finance" title="سطرهای تحویل مالی" />
                <V2Table
                  columns={handoffColumns}
                  rows={result.data.financeHandoff.lines.map((l, i) => ({ ...l, id: `${i}` }))}
                  zebra
                />
              </V2Card>

              <V2Card>
                <V2CardHeader
                  icon="invoice"
                  title="ثبت سند حسابداری"
                  subtitle="اگر سند حسابداری مرتبط با این حرکت هنوز ثبت نشده، اینجا ثبت کنید"
                />
                <V2CardBody>
                  <form className="inv-form" noValidate onSubmit={onRecordHandoff}>
                    {recordError ? <V2Alert tone="danger">{recordError}</V2Alert> : null}
                    {recordResult ? (
                      <V2Alert tone="success">
                        {recordResult.replayed
                          ? `این حرکت قبلاً ثبت شده بود — سند ${recordResult.provisionalNumber}`
                          : `سند پیش‌نویس ${recordResult.provisionalNumber} ثبت شد`}
                      </V2Alert>
                    ) : null}
                    <div className="inv-form__row">
                      <V2FormField label="حساب بدهکار" required width="full">
                        <select
                          className="sk-select"
                          value={debitAccountId}
                          onChange={(e) => setDebitAccountId(e.target.value)}
                        >
                          <option value="">انتخاب حساب</option>
                          {postableGlAccounts.map((a) => (
                            <option key={a.accountId} value={a.accountId}>
                              {a.code} — {a.name}
                            </option>
                          ))}
                        </select>
                      </V2FormField>
                      <V2FormField label="حساب بستانکار" required width="full">
                        <select
                          className="sk-select"
                          value={creditAccountId}
                          onChange={(e) => setCreditAccountId(e.target.value)}
                        >
                          <option value="">انتخاب حساب</option>
                          {postableGlAccounts.map((a) => (
                            <option key={a.accountId} value={a.accountId}>
                              {a.code} — {a.name}
                            </option>
                          ))}
                        </select>
                      </V2FormField>
                    </div>
                    <div className="inv-form__actions">
                      <V2Button type="submit" variant="primary" disabled={recording}>
                        {recording ? 'در حال ثبت…' : 'ثبت سند'}
                      </V2Button>
                    </div>
                  </form>
                </V2CardBody>
              </V2Card>
            </>
          ) : null}

          <RelatedJournalsCard
            base={base}
            sourceReferences={[result.data.movementId, result.data.movementNumber]}
            refreshKey={journalsRefreshKey}
          />
        </>
      ) : null}

      <V2ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
